import React, { useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { HUD_MESSAGE_MAX_DETAIL, HUD_MESSAGE_MAX_TEXT, SendHudMessageInput } from '../../services/hud';

const EXPIRY_OPTIONS = [2, 5, 10];
const COUNTER_FROM = 150;

const QUICK_SENDS = [
  'Time check: wrap up this section?',
  'Move on to the next block?',
  'Break now?',
  'Client wants a word at the break',
  'Demo ready to show?',
  'Running long: skip the next activity?',
];

interface HudComposerProps {
  /** Resolves true when the send succeeded; false restores the input. */
  onSend: (input: SendHudMessageInput) => Promise<boolean>;
}

export function HudComposer({ onSend }: HudComposerProps) {
  const [text, setText] = useState('');
  const [detail, setDetail] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [expires, setExpires] = useState(5);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    const trimmedDetail = detail.trim();
    setText('');
    setDetail('');
    setShowDetail(false);
    const ok = await onSend({
      text: trimmed,
      ...(trimmedDetail ? { detail: trimmedDetail } : {}),
      expiresInMinutes: expires,
    });
    if (!ok) {
      setText(trimmed);
      if (trimmedDetail) { setDetail(trimmedDetail); setShowDetail(true); }
    }
  };

  const fill = (value: string) => {
    setText(value);
    inputRef.current?.focus();
  };

  return (
    <section className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Ask the lead</h2>
        <span className="text-xs text-slate-400">The lead answers yes or no</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {QUICK_SENDS.map(q => (
          <button
            key={q}
            type="button"
            onClick={() => fill(q)}
            className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 hover:border-primary/40 hover:bg-primary/5 hover:text-navy transition-colors"
          >
            {q}
          </button>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value.slice(0, HUD_MESSAGE_MAX_TEXT))}
            maxLength={HUD_MESSAGE_MAX_TEXT}
            placeholder="Ask a yes/no question and press Enter"
            className="flex-1 min-w-0 border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          <select
            value={expires}
            onChange={e => setExpires(parseInt(e.target.value, 10))}
            title="Expires after"
            aria-label="Expires after"
            className="border border-[#e2e8f0] rounded-xl px-2 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            {EXPIRY_OPTIONS.map(m => <option key={m} value={m}>{m} min</option>)}
          </select>
          <button
            type="submit"
            disabled={!text.trim()}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={16} /> Send
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 min-h-[1rem]">
          {!showDetail ? (
            <button type="button" onClick={() => setShowDetail(true)} className="text-xs font-semibold text-primary hover:underline">
              Add detail
            </button>
          ) : <span />}
          {text.length > COUNTER_FROM && (
            <span className={`text-xs tabular-nums ${text.length >= HUD_MESSAGE_MAX_TEXT ? 'text-red-500' : 'text-slate-400'}`}>
              {text.length}/{HUD_MESSAGE_MAX_TEXT}
            </span>
          )}
        </div>
        {showDetail && (
          <div>
            <textarea
              value={detail}
              onChange={e => setDetail(e.target.value.slice(0, HUD_MESSAGE_MAX_DETAIL))}
              maxLength={HUD_MESSAGE_MAX_DETAIL}
              rows={2}
              autoFocus
              placeholder="Optional detail the wearer can open"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
            {detail.length > 0 && (
              <p className="mt-1 text-xs text-slate-400 text-right">{detail.length}/{HUD_MESSAGE_MAX_DETAIL}</p>
            )}
          </div>
        )}
      </form>
    </section>
  );
}
